import React from 'react';
import { Avatar, Message, MessageModel } from '@chatscope/chat-ui-kit-react';
import moment from 'moment';
import { LoadedAdminData } from 'client/admin/shared/types';
import { UserAvatar } from 'client/admin/shared/components/user-avatar';
import {
  AiAssistantMessage,
  AiAssistantMessageRole,
} from 'client/admin/core/ai-assistant-session';
import { MaterialIcon, MaterialIconName } from 'client/shared/components/base';
import './styles.scss';
import pollyParrotImage from 'client/assets/polly-parrot.svg';
import { MarkdownWithEmbeddedContent } from './embedded-content';
import { ClientPublishingEntityId } from 'client/shared/core/publishing-entity';
import { AiAssistant, AiAssistantName } from 'core';

const baseClass = 'pn-ai-assistant-message';
const ASSISTANT_NAME = 'Polly';

export const AiAssistantMessageComponent: React.FC<{
  readonly adminData: LoadedAdminData;
  readonly assistant: AiAssistant;
  readonly className?: string;
  readonly key?: string;
  readonly avatarOverride?: AvatarOverride;
  readonly aiAssistantMessage: AiAssistantMessage;
  readonly sessionId: string;
}> = (p) => {
  const sender = getSender(p.aiAssistantMessage.role, p.adminData);

  return (
    <Message
      avatarPosition={getAvatarPosition(p.aiAssistantMessage.role)}
      className={`${baseClass} ${p.className ?? ''} ${
        isSytem(p.aiAssistantMessage.role) ? 'system-message' : ''
      }`}
      key={p.key}
      model={aiAssistantMessageToMessage(
        p.aiAssistantMessage,
        sender,
        p.adminData.admin.activePublishingEntity.id,
        p.assistant,
        p.sessionId,
        p.adminData.admin.activePublishingEntity.fips ?? undefined
      )}
      type={'custom'}
    >
      {/* Avatar must be the only child */}
      <Avatar
        name={
          p.avatarOverride
            ? 'system'
            : getSender(p.aiAssistantMessage.role, p.adminData)
        }
        size={'sm'}
      >
        <CustomAvatar
          adminData={p.adminData}
          assistant={p.assistant}
          avatarOverride={p.avatarOverride}
          role={p.aiAssistantMessage.role}
        />
      </Avatar>
    </Message>
  );
};

export const CustomAvatar: React.FC<{
  readonly avatarOverride?: AvatarOverride;
  readonly adminData: LoadedAdminData;
  readonly assistant: AiAssistant;
  readonly role: AiAssistantMessageRole;
}> = ({ avatarOverride, adminData, assistant, role }) => {
  if (avatarOverride) {
    switch (avatarOverride) {
      case AvatarOverride.UPLOAD:
        return (
          <MaterialIcon
            className="text-grayscale-4"
            icon={MaterialIconName.ATTACH_FILE}
            iconType={'material-icons-outlined'}
          />
        );
    }
  }
  switch (role) {
    case AiAssistantMessageRole.ASSISTANT:
      switch (assistant.id) {
        case AiAssistantName.POLLY:
          return <img alt="polly avatar" src={pollyParrotImage} />;
        case AiAssistantName.GRANT_WRITER:
          return <UserAvatar name="Grace" />;
        case AiAssistantName.ANNIE:
          return <UserAvatar name="Annie" />;
        case AiAssistantName.SANDBOX:
        default:
          return <UserAvatar name="Assistant" />;
      }
    case AiAssistantMessageRole.USER:
      return <UserAvatar name={adminData.admin.name} />;
    case AiAssistantMessageRole.SYSTEM:
      return (
        <MaterialIcon
          className="text-grayscale-4"
          icon={MaterialIconName.INFO}
          iconType={'material-icons-outlined'}
        />
      );
  }
};

function aiAssistantMessageToMessage(
  aiAssistantMessage: AiAssistantMessage,
  sender: string,
  pubId: ClientPublishingEntityId,
  assistant: AiAssistant,
  sessionId: string,
  fipsId?: string,
  position?: MessageModel['position']
): MessageModel {
  return {
    payload: (
      <Message.CustomContent>
        <MarkdownWithEmbeddedContent
          assistant={assistant}
          children={aiAssistantMessage.content}
          className={`${baseClass}-markdown`}
          fipsId={fipsId}
          pubId={pubId}
          sessionId={sessionId}
        />
      </Message.CustomContent>
    ),
    sentTime: moment(aiAssistantMessage.timestamp).fromNow(),
    sender,
    direction: getMessageDirection(aiAssistantMessage.role),
    position: position ?? 'single',
  };
}

export function isAssistant(role: AiAssistantMessageRole) {
  return role === AiAssistantMessageRole.ASSISTANT;
}
export function isSytem(role: AiAssistantMessageRole) {
  return role === AiAssistantMessageRole.SYSTEM;
}

export function getPosition(
  idx: number,
  messageGroupLength: number
): MessageModel['position'] {
  if (idx === 0) {
    return 'first';
  } else if (idx === messageGroupLength - 1) {
    return 'last';
  }
  return 'single';
}

function getSender(role: AiAssistantMessageRole, adminData: LoadedAdminData) {
  switch (role) {
    case AiAssistantMessageRole.ASSISTANT:
      return ASSISTANT_NAME;
    case AiAssistantMessageRole.USER:
      return adminData.admin.name;
    case AiAssistantMessageRole.SYSTEM:
      return 'System';
  }
}

function getAvatarPosition(role: AiAssistantMessageRole) {
  switch (role) {
    case AiAssistantMessageRole.USER:
      return 'tr';
    case AiAssistantMessageRole.ASSISTANT:
    case AiAssistantMessageRole.SYSTEM:
      return 'tl';
  }
}

function getMessageDirection(role: AiAssistantMessageRole) {
  switch (role) {
    case AiAssistantMessageRole.USER:
      return 'outgoing';
    case AiAssistantMessageRole.ASSISTANT:
    case AiAssistantMessageRole.SYSTEM:
      return 'incoming';
  }
}

export enum AvatarOverride {
  UPLOAD = 'UPLOAD',
}
